import { useEffect, useState } from "react";
import { portalApi, Page } from "./PortalShell";
import { User, Mail, Phone, MapPin, Instagram, Award, Users, Calendar, KeyRound } from "lucide-react";
import PasskeySection from "../../pwa/PasskeySection";
import PortalSecuritySection from "./PortalSecuritySection";

function Row({ icon: Icon, label, value, testId }) {
  return (
    <div data-testid={testId} className="flex items-center gap-3 rounded-xl bg-neutral-900 border border-neutral-800 px-4 py-3">
      {Icon && <Icon className="h-4 w-4 text-neutral-500 shrink-0" />}
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-widest text-neutral-500">{label}</div>
        <div className="text-sm mt-0.5 truncate">{value || <span className="italic text-neutral-500">—</span>}</div>
      </div>
    </div>
  );
}

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : null);

export default function PortalSettingsPage() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    portalApi.get("/affiliate/settings").then((r) => setData(r.data)).catch((e) => setErr(e?.response?.data?.detail || "Load failed"));
  }, []);
  if (err) return <Page title="Settings"><div className="text-red-400">{err}</div></Page>;
  if (!data) return <Page title="Settings"><div className="text-neutral-500 italic">Loading…</div></Page>;

  const { profile: p, asm, portal: prt, codes } = data;

  return (
    <Page title="Settings" subtitle="View-only — to change anything, ping your ASM" testId="portal-settings-page">
      {/* Basic profile */}
      <div>
        <div className="text-xs font-semibold uppercase tracking-widest text-neutral-500 mb-3">Profile</div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <Row icon={User} label="Name" value={p.name} testId="setting-name" />
          <Row icon={Mail} label="Email" value={p.email} testId="setting-email" />
          <Row icon={Phone} label="Phone" value={p.phone} testId="setting-phone" />
          <Row icon={Instagram} label="Instagram" value={p.instagram ? `@${p.instagram}${p.followers ? ` · ${p.followers.toLocaleString("en-IN")} followers` : ""}` : null} testId="setting-instagram" />
          <Row icon={MapPin} label="City" value={p.city} testId="setting-city" />
          <Row icon={Award} label="Niche" value={p.niche} testId="setting-niche" />
        </div>
      </div>

      {/* Program status */}
      <div>
        <div className="text-xs font-semibold uppercase tracking-widest text-neutral-500 mb-3">Program Status</div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <Row icon={Award} label="Current Tier" value={p.tier} testId="setting-tier" />
          <Row label="Lifetime New Customers" value={p.lifetime_new_customers?.toLocaleString("en-IN")} testId="setting-lnc" />
          <Row icon={Calendar} label="Onboarding Call" value={fmt(p.onboarding_call_at)} testId="setting-onboarding-call" />
          <Row label="Fast Track Status" value={p.fast_track_status?.replace("_", " ")} testId="setting-fast-track" />
          <Row label="Pro Reached" value={fmt(p.pro_reached_at)} testId="setting-pro-at" />
          <Row label="Pro Advanced Unlocked" value={fmt(p.pro_advanced_unlocked_at)} testId="setting-pa-at" />
          <Row label="Legend Unlocked" value={fmt(p.legend_unlocked_at)} testId="setting-legend-at" />
          <Row label="Onboarding Stage" value={p.onboarding_stage} testId="setting-onboarding-stage" />
        </div>
      </div>

      {/* Discount codes */}
      {codes.length > 0 && (
        <div>
          <div className="text-xs font-semibold uppercase tracking-widest text-neutral-500 mb-3">Your Codes</div>
          <div className="space-y-2">
            {codes.map((c) => (
              <div key={c.code} className="rounded-xl bg-neutral-900 border border-neutral-800 px-4 py-3 flex items-center justify-between">
                <div className="min-w-0">
                  <div className="text-base font-mono font-bold tracking-wider">{c.code}</div>
                  {c.referral_link && <div className="text-[10px] text-neutral-500 truncate mt-0.5">{c.referral_link}</div>}
                </div>
                <div className="text-right shrink-0">
                  <div className="text-xs text-neutral-500">{c.usage_count || 0} uses</div>
                  <div className={`text-[10px] uppercase tracking-widest mt-0.5 ${c.active ? "text-emerald-400" : "text-neutral-500"}`}>
                    {c.active ? "Active" : "Inactive"}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Your ASM */}
      <div>
        <div className="text-xs font-semibold uppercase tracking-widest text-neutral-500 mb-3">Your Success Manager</div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <Row icon={Users} label="Name" value={asm.name} testId="setting-asm-name" />
          <Row icon={Mail} label="Email" value={asm.email} testId="setting-asm-email" />
        </div>
      </div>

      {/* Portal account */}
      <div>
        <div className="text-xs font-semibold uppercase tracking-widest text-neutral-500 mb-3">Portal Account</div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <Row icon={KeyRound} label="Login Email" value={prt.email} testId="setting-portal-email" />
          <Row icon={Calendar} label="Portal Access Since" value={fmt(prt.created_at)} testId="setting-portal-created" />
          <Row icon={Calendar} label="Last Login" value={prt.last_login_at ? new Date(prt.last_login_at).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit" }) : "First login"} testId="setting-portal-last-login" />
        </div>
        <div className="text-[11px] text-neutral-500 mt-3">
          Need to change your password or profile? Message your ASM — self-serve editing coming soon.
        </div>
      </div>

      {/* Passkey / Face ID setup */}
      <PasskeySection />

      {/* Change password + Sign out everywhere */}
      <PortalSecuritySection mustChangePassword={!!prt.must_change_password} />
    </Page>
  );
}
